import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Locale, LOCALES, Translations, Currency } from "./types";
import { ptBR } from "./locales/pt-BR";
import { en } from "./locales/en";
import { ja } from "./locales/ja";

// ============================================
// Exchange Rates (BRL as base)
// Update periodically via API
// ============================================

export interface ExchangeRates {
  BRL: number;
  USD: number;
  JPY: number;
  lastUpdated: string;
}

// Default fallback rates (approximately jan 2026)
export const DEFAULT_RATES: ExchangeRates = {
  BRL: 1,
  USD: 0.17,  // 1 BRL = ~0.17 USD
  JPY: 26.5,  // 1 BRL = ~26.5 JPY
  lastUpdated: new Date().toISOString(),
};

// ============================================
// i18n Store
// ============================================

interface I18nState {
  locale: Locale;
  translations: Translations;
  exchangeRates: ExchangeRates;
  isLoadingRates: boolean;
  
  // Actions
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  
  // Currency
  formatCurrency: (amountInCentsBRL: number, targetCurrency?: Currency) => string;
  convertPrice: (amountInCentsBRL: number, toCurrency: Currency) => number;
  updateExchangeRates: (rates: ExchangeRates) => void;
}

const translations: Record<Locale, Translations> = {
  "pt-BR": ptBR,
  "en": en,
  "ja": ja,
};

// Helper to get nested translation value
function getNestedTranslation(obj: any, path: string): string {
  const keys = path.split(".");
  let value = obj;
  
  for (const key of keys) {
    if (value && typeof value === "object" && key in value) {
      value = value[key];
    } else {
      return path; // Fallback to key path if not found
    }
  }
  
  return typeof value === "string" ? value : path;
}

export const useI18n = create<I18nState>()(
  persist(
    (set, get) => ({
      locale: "pt-BR",
      translations: ptBR,
      exchangeRates: DEFAULT_RATES,
      isLoadingRates: false,
      
      setLocale: (locale: Locale) => {
        set({
          locale,
          translations: translations[locale],
        });
      },
      
      t: (key: string, params?: Record<string, string | number>) => {
        const { translations: trans } = get();
        let text = getNestedTranslation(trans, key);
        
        // Replace parameters
        if (params) {
          Object.entries(params).forEach(([param, value]) => {
            text = text.replace(`{${param}}`, String(value));
          });
        }
        
        return text;
      },
      
      formatCurrency: (amountInCentsBRL: number, targetCurrency?: Currency): string => {
        const { exchangeRates, locale } = get();
        const localeConfig = LOCALES[locale];
        const currencyToUse = targetCurrency || localeConfig.currency;
        
        // Convert from BRL cents to value in target currency
        const amountInBRL = amountInCentsBRL / 100;
        const convertedAmount = amountInBRL * exchangeRates[currencyToUse];
        
        // Get currency symbol for target currency
        let symbol = localeConfig.currencySymbol;
        if (targetCurrency) {
          const targetLocale = Object.entries(LOCALES).find(
            ([_, config]) => config.currency === targetCurrency
          );
          if (targetLocale) {
            symbol = LOCALES[targetLocale[0] as Locale].currencySymbol;
          }
        }
        
        // Format based on currency
        let formatted: string;
        if (currencyToUse === "JPY") {
          // JPY doesn't use decimals
          formatted = Math.round(convertedAmount).toLocaleString(locale);
        } else {
          // USD and BRL use 2 decimals
          formatted = convertedAmount.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        
        // Format with currency symbol based on locale
        if (locale === "pt-BR") {
          return `${symbol} ${formatted}`; // R$ 10,00
        } else if (locale === "en") {
          return `${symbol}${formatted}`; // $10.00
        } else {
          return `${formatted}${symbol}`; // 10,00¥
        }
      },
      
      convertPrice: (amountInCentsBRL: number, toCurrency: Currency): number => {
        const { exchangeRates } = get();
        const amountInBRL = amountInCentsBRL / 100;
        const convertedAmount = amountInBRL * exchangeRates[toCurrency];
        
        // Round appropriately
        if (toCurrency === "JPY") {
          return Math.round(convertedAmount);
        } else {
          return Math.round(convertedAmount * 100) / 100;
        }
      },
      
      updateExchangeRates: (rates: ExchangeRates) => {
        set({ exchangeRates: rates });
      },
    }),
    {
      name: "rocketanks-i18n",
      partialize: (state) => ({
        locale: state.locale,
        exchangeRates: state.exchangeRates,
      }),
    }
  )
);

// Convenience hook exports
export const useLocale = () => useI18n((state) => state.locale);
export const useTranslations = () => useI18n((state) => state.translations);
export const useT = () => useI18n((state) => state.t);
export const useFormatCurrency = () => useI18n((state) => state.formatCurrency);
export const useConvertPrice = () => useI18n((state) => state.convertPrice);
export const useSetLocale = () => useI18n((state) => state.setLocale);
